import { useEffect } from "react";
import { useMediaQuery } from "@/hooks/useMediaQuery";

/**
 * Sets <meta name="theme-color"> AND html background for a specific page.
 *
 * - Pass a hex color to override (e.g. accent-tinted public profiles).
 * - Pass `undefined` to use the default (CSS handles light/dark automatically).
 *
 * On unmount, clears the override so CSS `html` / `html.dark` rules take over.
 * Reacts to system dark mode changes so the override stays correct.
 */
const DEFAULT_LIGHT = "#fcfbfd";
const DEFAULT_DARK = "#110f18";

function setMetas(color: string) {
  document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')
    .forEach((m) => m.setAttribute("content", color));
}

export function useThemeColor(color: string | undefined) {
  const prefersDark = useMediaQuery("(prefers-color-scheme: dark)");

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    if (color) {
      // Page-specific override (e.g. accent-colored public profile)
      // Set both html & body so overscroll / rubber-band areas match
      html.style.backgroundColor = color;
      body.style.backgroundColor = color;
      setMetas(color);
    } else {
      // No override — let CSS handle it, just sync the meta tags
      html.style.removeProperty("background-color");
      body.style.removeProperty("background-color");
      setMetas(prefersDark ? DEFAULT_DARK : DEFAULT_LIGHT);
    }

    return () => {
      // Always clean up: remove inline style, reset metas
      html.style.removeProperty("background-color");
      body.style.removeProperty("background-color");
      const fallback = document.documentElement.classList.contains("dark")
        ? DEFAULT_DARK : DEFAULT_LIGHT;
      setMetas(fallback);
    };
  }, [color, prefersDark]);
}
